/**
 * SAVED VIEWS — QA-07.
 *
 * "The dashboard combines period, granularity, comparison, scope, 30+ advanced
 * filter controls, KPI tiles, five sections, downloads, notifications and IQ
 * entry points on one surface." The owner's answer was progressive disclosure,
 * and the half that makes disclosure survivable is this: a reader who has
 * assembled the twelve-control filter they need every Monday should not have to
 * reassemble it every Monday.
 *
 * ── WHAT A VIEW IS ──
 *
 * A name and the screen's own query string. Loading one is exactly a navigation
 * to that address — the same act as pasting a link somebody sent — so there is
 * no second code path that restores a filter and no second encoding to keep in
 * step. It PUSHES rather than replaces: loading a view is a discrete choice, and
 * Back should return the reader to where they were.
 *
 * ── WHY IT DOES NOT LOOK LIKE A DROPDOWN OF ACTIONS ──
 *
 * A saved view has two verbs (load, delete) and the destructive one must not sit
 * one pixel from the ordinary one in a list somebody scans quickly. So the row
 * is a button, and Delete is a separate small control with its own accessible
 * name that says which view it removes.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from '../../../../../utils/toast';
import { useMenuDismiss } from '../../../../hooks/use-menu-dismiss';
import { overlayMotion, Presence } from '../../../../../motion';
import { SavedView, deleteView, listViews, saveView } from '../../../../../api/views-client';
import {
  ViewsBar,
  ViewsButton,
  ViewsDelete,
  ViewsEmpty,
  ViewsList,
  ViewsMenu,
  ViewsNameInput,
  ViewsRow,
  ViewsSaveRow,
  ViewsShareLabel,
} from '../dashboard-screen.styled';

/* The menu grows from the trigger's corner and shrinks back into it. */
const POPOVER = overlayMotion('popover');
const POPOVER_ORIGIN = { transformOrigin: 'top right' } as const;

export interface SavedViewsProps {
  /** The filter currently on screen, as a query string without the '?'. */
  search: string;
  /** Navigate to a saved view's filter. Pushes — see the header. */
  onLoad: (search: string) => void;
}

export const SavedViews: React.FC<SavedViewsProps> = ({ search, onLoad }) => {
  const [open, setOpen] = useState(false);
  const [views, setViews] = useState<SavedView[] | null>(null);
  const [name, setName] = useState('');
  const [shared, setShared] = useState(false);
  const [busy, setBusy] = useState(false);

  /* Loaded when the menu is FIRST opened, not on mount. This control is on a
     screen that already fires a batch, an overview and a records query on every
     filter change; a list nobody has asked to see is not worth a request on
     every dashboard load. */
  const load = useCallback(async () => {
    try {
      setViews(await listViews());
    } catch {
      // `null` stays "not loaded" and would spin forever, so an empty list is
      // the honest degraded state — paired with the message below, which says
      // the difference rather than showing "no saved views" over a failure.
      setViews([]);
      toast.error('Could not load your saved views.');
    }
  }, []);

  useEffect(() => {
    if (open && views === null) void load();
  }, [open, views, load]);

  /* Escape AND outside click, from the shared helper the export and columns
     menus use. A press that begins INSIDE the menu — on the name input, on a
     row — is not an outside click and never closes it; the trigger is excluded
     too, because a press there is a toggle and must not be handled twice. */
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => setOpen(false), []);

  useMenuDismiss(open, close, { menu: menuRef, trigger: triggerRef });

  const onSave = async () => {
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    try {
      const saved = await saveView({ name: trimmed, search, is_shared: shared });
      setViews(current => {
        const rest = (current || []).filter(v => v.id !== saved.id);

        return [...rest, saved].sort((a, b) => a.name.localeCompare(b.name));
      });
      setName('');
      toast.success(`Saved “${saved.name}”.`);
    } catch (error) {
      const shaped = error as { response?: { data?: { message?: string } } };
      // The server's own sentence when it has one: "You already have 50 saved
      // views" tells the reader what to do, where "could not save" does not.
      toast.error(shaped?.response?.data?.message || 'Could not save that view.');
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (view: SavedView) => {
    if (busy) return;
    setBusy(true);
    try {
      await deleteView(view.id);
      setViews(current => (current || []).filter(v => v.id !== view.id));
      toast.success(`Deleted “${view.name}”.`);
    } catch (error) {
      const shaped = error as { response?: { data?: { message?: string } } };
      toast.error(shaped?.response?.data?.message || 'Could not delete that view.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ViewsBar>
      <ViewsButton
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen(!open)}
        data-testid="saved-views-trigger"
      >
        Saved views
      </ViewsButton>

      <Presence>
      {open && (
        <ViewsMenu
          key="saved-views-menu"
          ref={menuRef}
          {...POPOVER}
          style={POPOVER_ORIGIN}
          role="group"
          aria-label="Saved views"
          data-testid="saved-views-menu"
        >
          <ViewsSaveRow>
            <ViewsNameInput
              aria-label="Name for this view"
              placeholder="Name this view"
              maxLength={80}
              value={name}
              onChange={event => setName(event.target.value)}
              onKeyDown={event => {
                if (event.key === 'Enter') void onSave();
              }}
            />
            <ViewsButton type="button" disabled={!name.trim() || busy} onClick={onSave}>
              Save
            </ViewsButton>
          </ViewsSaveRow>

          <ViewsShareLabel>
            <input
              type="checkbox"
              checked={shared}
              onChange={event => setShared(event.target.checked)}
            />
            {/* Says what sharing DOES, not just that it is on. A checkbox
                labelled "Shared" leaves the reader to guess whether their
                colleagues can also change it — they cannot, and that is worth
                one clause. */}
            <span>Share with my organisation (they can use it; only you can change it)</span>
          </ViewsShareLabel>

          <ViewsList>
            {views === null ? (
              <ViewsEmpty>Loading…</ViewsEmpty>
            ) : views.length === 0 ? (
              <ViewsEmpty>
                No saved views yet. Set the filters you want, name them above and
                press Save.
              </ViewsEmpty>
            ) : (
              views.map(view => (
                <ViewsRow key={view.id}>
                  <ViewsButton
                    type="button"
                    onClick={() => {
                      onLoad(view.search);
                      setOpen(false);
                    }}
                    title={view.is_shared ? 'Shared with your organisation' : undefined}
                  >
                    {view.name}
                    {view.is_shared ? ' · shared' : ''}
                  </ViewsButton>
                  {view.is_mine && (
                    <ViewsDelete
                      type="button"
                      aria-label={`Delete the saved view ${view.name}`}
                      disabled={busy}
                      onClick={() => onDelete(view)}
                    >
                      ×
                    </ViewsDelete>
                  )}
                </ViewsRow>
              ))
            )}
          </ViewsList>
        </ViewsMenu>
      )}
      </Presence>
    </ViewsBar>
  );
};
